import React, {MouseEventHandler, useCallback, useEffect, useState} from 'react';
import cn from 'classnames';
import {Trans} from '@lingui/react';
import {t} from '@lingui/core/macro';


import HintQuestion from '../Hint/HintQuestion';

import styles from './StatusToggle.module.scss';

interface IStatusToggleProps {
    condition: boolean,
    onClick: MouseEventHandler<HTMLDivElement>,
    disabled?: boolean,
    isMonitor?: boolean,
    textActive?: string,
    notActiveText?: string,
    preventToggle?: boolean,
    errorToggle?: boolean,
    massAddHint?: string,
}

const StatusToggle: React.FC<IStatusToggleProps> = ({
    condition,
    onClick,
    disabled = false,
    isMonitor = false,
    textActive = t`Активен`,
    notActiveText = t`Неактивен`,
    preventToggle = false,
    errorToggle=false,
    massAddHint,
}) => {
    const [localCondition, setLocalCondition] = useState(condition);

    // eslint-disable-next-line max-len
    const TEXT_ARIA = massAddHint || t`Каждый URL с новой строки. URL должны начинаться с http или https, а также не должны содержать пробелов и других недопустимых для URL-формата символов.`;

    useEffect(() => {
        setLocalCondition(condition);
    }, [condition]);

    const handleClick = useCallback<MouseEventHandler<HTMLDivElement>>((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled || !onClick) return;

        if (!preventToggle) {
            setLocalCondition(prev => !prev);
        }

        onClick(e);
    }, [disabled, onClick, preventToggle]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (disabled || !onClick) return;
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();

            if (!preventToggle) {
                setLocalCondition(prev => !prev);
            }

            onClick(e as any);
        }
    }, [disabled, onClick, preventToggle]);

    const getStatusText = () => {
        if (isMonitor) {
            return localCondition ? (
                <div className={styles.blockWhitQuestion}>
                    <Trans id="add.monitor.modal.toggle.status" message="Массовое добавление"/>
                    <HintQuestion placement="top-start">
                        {TEXT_ARIA}
                    </HintQuestion>
                </div>
            ) : (
                <>
                    <Trans id="add.monitor.modal.toggle.status" message="Массовое добавление"/>
                </>
            );
        }
        return localCondition ? textActive : notActiveText;
    };

    return (
        <div
            role="switch"
            aria-checked={localCondition}
            aria-disabled={disabled}
            className={cn(
                styles.container,
                styles.color,
            )}
            tabIndex={disabled ? undefined : 0}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
        >
            <div
                className={cn(
                    styles.toggle,
                    errorToggle && styles.errorToggle,
                    localCondition  && styles.active,
                    disabled && styles.disabled,
                )}
                title={localCondition ? t`Деактивировать` : t`Активировать`}
            />
            <span className={cn(
                styles.text,
                errorToggle && styles.errorText,
                localCondition  && styles.active,
                disabled && styles.disabled,
            )}>
                {getStatusText()}
            </span>
        </div>
    );
};

export default React.memo(StatusToggle);